
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Journalist } from "@/components/reader-home/types";

// Import our new components
import SidebarSkeleton from './SidebarSkeleton';
import JournalistItem from './JournalistItem';
import SuggestedJournalistItem from './SuggestedJournalistItem';
import EmptyFollowingState from './EmptyFollowingState';

interface JournalistSidebarProps {
  journalists: Journalist[];
  suggestedJournalists?: Journalist[];
  loading?: boolean;
  onFollowJournalist?: (journalistId: string) => void;
  onSeeAllClick?: () => void;
}

const JournalistSidebar: React.FC<JournalistSidebarProps> = ({ 
  journalists,
  suggestedJournalists = [],
  loading = false,
  onFollowJournalist,
  onSeeAllClick = () => { window.location.href = '/authors'; }
}) => {
  const navigate = useNavigate();
  
  // Debug output for followed journalists
  useEffect(() => {
    console.log('JournalistSidebar - Journalists:', journalists);
    console.log('JournalistSidebar - Suggested:', suggestedJournalists);
  }, [journalists, suggestedJournalists]);

  const handleSeeAllClick = () => {
    if (onSeeAllClick) {
      onSeeAllClick();
    } else {
      navigate('/authors');
    }
  };

  if (loading) {
    return <SidebarSkeleton />;
  }

  return (
    <aside className="hidden md:flex w-64 flex-col p-4 border-r border-border bg-card">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Following</h2>
        {journalists.length > 0 && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleSeeAllClick}
          >
            See All
          </Button>
        )}
      </div>
      
      {journalists && journalists.length > 0 ? (
        <div className="space-y-3">
          {journalists.map(journalist => (
            <JournalistItem key={journalist.id} journalist={journalist} />
          ))}
        </div>
      ) : suggestedJournalists && suggestedJournalists.length > 0 ? (
        <>
          <h3 className="text-sm font-medium text-muted-foreground mb-3">Suggested Authors to Follow</h3>
          <div className="space-y-3">
            {suggestedJournalists.slice(0, 5).map(journalist => (
              <SuggestedJournalistItem 
                key={journalist.id} 
                journalist={journalist} 
                onFollowJournalist={id => onFollowJournalist && onFollowJournalist(id)} 
              />
            ))}
          </div>
        </>
      ) : (
        <EmptyFollowingState />
      )}
    </aside>
  );
};

export default JournalistSidebar;
